import { useEffect, useMemo, useState } from 'react';
import { Col, DatePicker, Divider, Form, Modal, Row, Switch } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import moment from 'moment';
import { dateToDayjs, dateToStringWT, getExpiresFromString } from '@/utils';
import { useGetLettersRecommendQuery, useGetRecommendationTypesQuery } from '@/services/general-api';
import Select from '@/components/select';
import { ProfileCol, ProfileRow } from '@/components/profile-grid';
import { useGetParentFosterCareQuery, useSaveParentRecommendationMutation } from '@/services/parent-api';

const EditModal = ({ parentId, editInfo, hasLicense, refetch, onClose }) => {
  const { data: recommendData } = useGetLettersRecommendQuery();
  const { data: typeData, isLoading: loadingTypeData } = useGetRecommendationTypesQuery();
  const [saveRecommend, { isLoading: saving }] = useSaveParentRecommendationMutation();
  const [selectedTypeName, setSelectedTypename] = useState('');
  const { data: currentFosterLicense, isLoading: loadingFoster } = useGetParentFosterCareQuery({ parentId });
  const [form] = Form.useForm();

  const [approved, setApproved] = useState(dayjs());

  useEffect(() => {
    if (editInfo) {
      setTimeout(() => {
        form.resetFields();
      });
    }
  }, [editInfo, form]);

  const initialValues = useMemo(() => {
    if (!editInfo) return {};
    const values = _.cloneDeep(editInfo);
    values.approved = dateToDayjs(editInfo?.approved) || dayjs();
    setApproved(dateToStringWT(values.approved));
    if (!editInfo?.parentRecommendationHistoryId) {
      values.recipients = [{ sendTo: true }];
    }
    return values;
  }, [editInfo]);

  const handleTypeChange = (recId) => {
    typeData.forEach((item) => {
      if (item.id === recId) {
        setSelectedTypename(item.name);
      }
    });
  };

  const onFinish = async (body) => {
    const date = hasLicense
      ? moment(currentFosterLicense.expires)
      : moment(getExpiresFromString(approved, 2), ['MM/DD/YYYY', 'M/D/YYYY']);
    body.expires = date;
    const date1 = moment(approved, ['MM/DD/YYYY', 'M/D/YYYY']);
    body.approved = date1;
    body.recipients[0] = { ...recommendData?.recipients[0], sendTo: body.recipients[0].sendTo };

    if (
      (selectedTypeName === 'Licensed Foster Parent' || selectedTypeName === 'Licensed to Adopt Only') &&
      hasLicense === false
    )
      return;

    try {
      await saveRecommend({ parentId, id: editInfo.parentRecommendationHistoryId, body }).unwrap();
    } catch (err) {}
    refetch();
    onClose();
  };

  return (
    <>
      <Modal
        title={editInfo?.parentRecommendationHistoryId ? 'Edit recommendation' : 'Recommend parent'}
        open={!!editInfo}
        confirmLoading={saving}
        onOk={() => form.submit()}
        onCancel={onClose}
        width={600}
      >
        <Form layout="vertical" form={form} initialValues={initialValues} loading={loadingFoster} onFinish={onFinish}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name={['recommendationType', 'id']} label="Type" rules={[{ required: true }]}>
                <Select options={typeData} loading={loadingTypeData} onChange={handleTypeChange} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="approved" label="Approved" rules={[{ required: true }]}>
                <DatePicker
                  format="M/D/YYYY"
                  onBlur={(e) => {
                    const inputVal = e.target.value;
                    const validDate = moment(dateToStringWT(inputVal), 'MM/DD/YYYY');
                    if (validDate.isValid()) {
                      // setEffective(validDate);
                      form.setFieldsValue({
                        approved: validDate,
                      });
                      setApproved(dateToStringWT(validDate));
                    } else {
                      form.setFieldsValue({
                        approved: null,
                      });
                    }
                  }}
                  onChange={(day) => {
                    setApproved(dateToStringWT(day));
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              {(selectedTypeName === 'Licensed Foster Parent' || selectedTypeName === 'Licensed to Adopt Only') && (
                <div>
                  {hasLicense ? (
                    <span style={{ color: 'green' }}>This parent has a valid foster care license.</span>
                  ) : (
                    <span style={{ color: 'orange' }}>This parent does not have a valid foster care license.</span>
                  )}
                </div>
              )}
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                noStyle
                // shouldUpdate={(prevValues, currentValues) => prevValues.approved !== currentValues.approved}
              >
                {hasLicense ? (
                  <ProfileCol
                    // md={{ spa: 12, offset: 12 }}
                    label="Expires"
                    value={moment(currentFosterLicense?.expires).format('M/D/YYYY')}
                  />
                ) : (
                  <ProfileCol
                    // md={{ spa: 12, offset: 12 }}
                    label="Expires"
                    value={getExpiresFromString(approved, 2)}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <ProfileRow>
            <ProfileCol md={12} label="Letter" value={recommendData?.name} />
            <ProfileCol md={12} label="Recipient" value={recommendData?.recipients[0].recipientType.name} />
            <ProfileCol md={{ span: 12, offset: 12 }} label="Send To">
              <Form.Item name={['recipients', 0, 'sendTo']} valuePropName="checked" noStyle>
                <Switch defaultChecked={true} />
              </Form.Item>
            </ProfileCol>
          </ProfileRow>
        </Form>
      </Modal>
    </>
  );
};

export default EditModal;
